/**
 * Ordine manuale a mercato dal portafoglio (TODO.md).
 *
 * ## Solo ordini a mercato, di proposito
 *
 * `placeOrder` supporta anche limit e stop, ma qui si espone **soltanto**
 * `market`, su scelta dell'utente: un ordine a mercato ha un solo parametro da
 * sbagliare, mentre limit e stop ne aggiungono tre e ognuno ha modi silenziosi
 * di non eseguire mai. Si aggiungeranno se serviranno.
 *
 * ## Due passi, non uno
 *
 * Prima l'**anteprima** — prezzo di riferimento, quantità effettiva dopo il
 * troncamento, controvalore stimato, budget dei limiti — poi l'invio. Il calcolo
 * lo fa il server e non questo componente: sono le stesse regole che decideranno
 * l'esito dell'invio, e duplicarle qui le farebbe divergere alla prima modifica,
 * con l'anteprima che dice una cosa e l'ordine che ne fa un'altra.
 *
 * **L'anteprima non è un'autorizzazione**: i limiti si consumano davvero solo
 * all'invio. Fra i due momenti un'altra operazione può esaurire il budget, e
 * l'invio può rifiutare qualcosa che l'anteprima aveva approvato — mai il
 * contrario.
 *
 * ## Importo o quantità
 *
 * Entrambi, a scelta. Chi ragiona in «quanto ci metto» scrive un importo e vede
 * la quantità che ne deriva; chi ragiona in azioni scrive la quantità. In
 * entrambi i casi la quantità mostrata è quella **troncata verso il basso** che
 * verrà davvero inviata: arrotondare per eccesso farebbe spendere più
 * dell'importo indicato.
 */

import { useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, Field, Modal, Select } from '@/ui';
import { BrokerTickerField } from './BrokerTickerField';
import { usePlaceOrder, usePreviewOrder, type OrderPreview } from './api';
import './manual-order.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/** Il codice resta accanto alla frase: due pubblici, due bisogni. */
const BLOCKER_LABEL: Record<string, string> = {
  no_price: 'nessun prezzo di riferimento disponibile per questo titolo',
  no_broker_ticker: 'manca la corrispondenza con il ticker del broker',
  trading_disabled: 'il trading è disabilitato su questa connessione',
  live_not_allowed: 'il trading su ambiente live non è abilitato sul server',
  quantity_too_small: "l'importo è troppo piccolo per il prezzo del titolo",
  limit_exceeded: 'supererebbe i limiti operativi che hai configurato',
  no_position: 'non risulta nessuna posizione da vendere su questo titolo',
};

const WARNING_LABEL: Record<string, string> = {
  stale_price: "il prezzo di riferimento ha più di un'ora: il controvalore mostrato è indicativo",
  residual_quantity: 'il broker non accetta abbastanza decimali per vendere tutto: resterà una frazione di azione',
};

type OrderMode = 'amount' | 'quantity' | 'all' | 'percent';

/**
 * I quarti della posizione (F-139): sono quelli che una persona ha in testa
 * quando decide «quanto» vendere, e quattro pulsanti si scelgono senza
 * digitare. Una percentuale libera resta possibile per quantita' o importo.
 */
const SELL_PERCENT_PRESETS = [25, 50, 75, 100] as const;
type SellPercent = (typeof SELL_PERCENT_PRESETS)[number];

export type ManualOrderModalProps = {
  connectionId: string;
  /** Titoli fra cui scegliere: posizioni del portafoglio e candidati in wishlist. */
  instruments: Array<{ id: string; name: string; symbol: string | null; trading212Ticker?: string | null }>;
  /**
   * Aperto **dentro** una posizione (F-136): il titolo è già deciso e non si
   * sceglie. ⚠️ Il campo resta visibile ma disabilitato invece di sparire: chi
   * ha aperto il modal da una pagina deve poter verificare su cosa sta per
   * operare, e un titolo implicito è il genere di cosa che si scopre dopo.
   */
  lockedInstrumentId?: string;
  defaultSide?: 'buy' | 'sell';
  onClose: () => void;
};

export function ManualOrderModal({
  connectionId,
  instruments,
  lockedInstrumentId,
  defaultSide,
  onClose,
}: ManualOrderModalProps) {
  const previewMutation = usePreviewOrder();
  const place = usePlaceOrder();

  const [instrumentId, setInstrumentId] = useState(lockedInstrumentId ?? '');
  const [side, setSide] = useState<'buy' | 'sell'>(defaultSide ?? 'buy');
  const [mode, setMode] = useState<OrderMode>('amount');
  const [value, setValue] = useState('');
  // La frazione della posizione (F-139). Parte dal 100 e non da un quarto:
  // chi entra in vendita da una posizione, in mancanza di una scelta, vende
  // tutto — ed e' anche l'unico dei quattro che coincide con «vendi tutta».
  const [percent, setPercent] = useState<SellPercent>(100);
  const [preview, setPreview] = useState<OrderPreview | null>(null);

  const instrument = instruments.find((item) => item.id === instrumentId);

  // Ogni modifica invalida l'anteprima: lasciarla a schermo dopo aver cambiato
  // il lato o l'importo mostrerebbe numeri che non corrispondono più a ciò che
  // verrebbe inviato — ed è l'anteprima stessa a perdere ogni valore.
  const invalidate = <T,>(setter: (next: T) => void) => (next: T) => {
    setPreview(null);
    setter(next);
  };

  // ⚠️ «Vendi tutto» esiste solo in vendita: cambiando lato si torna
  // all'importo, invece di lasciare un modo attivo che il server rifiuterebbe.
  const setSideSafely = (next: 'buy' | 'sell') => {
    setPreview(null);
    setSide(next);
    if (next === 'buy' && (mode === 'all' || mode === 'percent')) setMode('amount');
  };

  const runPreview = () => {
    if (!instrumentId) return;
    if ((mode === 'amount' || mode === 'quantity') && value.trim() === '') return;
    previewMutation.mutate(
      {
        connectionId,
        instrumentId,
        side,
        ...(mode === 'all'
          ? { sellAll: true }
          : mode === 'percent'
            ? { sellPercent: percent }
            : mode === 'amount'
              ? { amount: value.trim() }
              : { quantity: value.trim() }),
      },
      { onSuccess: setPreview },
    );
  };

  const submit = () => {
    if (!preview?.canSubmit || !preview.quantity) return;
    place.mutate(
      { connectionId, ticker: preview.ticker, side, quantity: Number(preview.quantity) },
      { onSuccess: onClose },
    );
  };

  const isLive = preview?.environment === 'live';

  return (
    <Modal title="Ordine manuale a mercato" onClose={onClose}>
      <div className="manual-order">
        <Select
          label="Titolo"
          value={instrumentId}
          onChange={(event) => invalidate(setInstrumentId)(event.target.value)}
          options={instruments.map((item) => ({
            value: item.id,
            label: item.symbol ? `${item.symbol} — ${item.name}` : item.name,
          }))}
          placeholder="Scegli un titolo…"
          disabled={Boolean(lockedInstrumentId)}
          hint={lockedInstrumentId ? 'Aperto dalla posizione: il titolo è già scelto.' : undefined}
          required
        />

        <Select
          label="Operazione"
          value={side}
          onChange={(event) => setSideSafely(event.target.value as 'buy' | 'sell')}
          options={[
            { value: 'buy', label: 'Acquisto' },
            { value: 'sell', label: 'Vendita' },
          ]}
        />

        <Select
          label="Come indichi l'ordine"
          value={mode}
          onChange={(event) => invalidate(setMode)(event.target.value as OrderMode)}
          options={[
            { value: 'amount', label: 'Per importo' },
            { value: 'quantity', label: 'Per quantità' },
            // ⚠️ Solo in vendita: non esiste un «compra tutto» né un quarto di niente.
            ...(side === 'sell'
              ? [
                  { value: 'percent', label: 'Per percentuale della posizione' },
                  { value: 'all', label: 'Vendi tutta la posizione' },
                ]
              : []),
          ]}
          hint={
            mode === 'all'
              ? 'La quantità la prende il sistema dalla posizione posseduta, così non resta indietro nulla per un arrotondamento.'
              : mode === 'percent'
                ? 'La quantità è la frazione della posizione posseduta, troncata ai decimali che il broker accetta. Il 100% equivale a «vendi tutta la posizione».'
                : mode === 'amount'
                  ? 'La quantità viene calcolata al prezzo di riferimento e troncata per difetto, così non si supera mai l’importo indicato.'
                  : 'La quantità viene troncata ai decimali che il broker accetta.'
          }
        />

        {mode === 'percent' && (
          <div className="manual-order__presets" role="group" aria-label="Percentuale della posizione">
            {SELL_PERCENT_PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant={percent === preset ? 'primary' : 'secondary'}
                aria-pressed={percent === preset}
                onClick={() => invalidate(setPercent)(preset)}
              >
                {preset}%
              </Button>
            ))}
          </div>
        )}

        {(mode === 'amount' || mode === 'quantity') && (
          <Field
            label={mode === 'amount' ? 'Importo' : 'Quantità'}
            value={value}
            onChange={(event) => invalidate(setValue)(event.target.value)}
            numeric
          />
        )}

        {/* Ricerca a catalogo dentro il form, su scelta dell'utente: senza
            mappatura l'ordine non può partire, e mandare l'utente su un'altra
            pagina a metà di un ordine è il modo più sicuro di fargli perdere
            quello che stava facendo. La conferma resta esplicita. */}
        {instrument && (
          <BrokerTickerField
            instrumentId={instrument.id}
            instrumentName={instrument.name}
            currentTicker={instrument.trading212Ticker ?? null}
            defaultQuery={instrument.symbol ?? instrument.name}
          />
        )}

        <div className="manual-order__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={runPreview}
            disabled={
              previewMutation.isPending ||
              !instrumentId ||
              ((mode === 'amount' || mode === 'quantity') && value.trim() === '')
            }
          >
            {previewMutation.isPending ? 'Calcolo…' : 'Calcola anteprima'}
          </Button>
        </div>

        {previewMutation.error && (
          <Alert variant="error" title="Anteprima non riuscita">
            {errorMessage(previewMutation.error, 'Errore imprevisto')}
          </Alert>
        )}

        {preview && (
          <div className="manual-order__preview">
            <div className="manual-order__row">
              <span>Prezzo di riferimento</span>
              <strong>{preview.referencePrice ?? '—'}</strong>
            </div>
            {preview.referencePriceAt && (
              <div className="manual-order__row manual-order__row--muted">
                <span>rilevato</span>
                <span>{formatDateTime(preview.referencePriceAt)}</span>
              </div>
            )}
            {preview.heldQuantity && (
              <div className="manual-order__row">
                <span>Posizione posseduta</span>
                <strong>{preview.heldQuantity}</strong>
              </div>
            )}
            {/* ⚠️ La data della posizione non e' decorazione: e' la fotografia
                dell'ultima sincronizzazione, non il conto di adesso. Fra quel
                momento e l'invio il broker puo' aver eseguito altro. */}
            {preview.heldAsOf && (
              <div className="manual-order__row manual-order__row--muted">
                <span>rilevata</span>
                <span>{formatDateTime(preview.heldAsOf)}</span>
              </div>
            )}
            <div className="manual-order__row">
              <span>Quantità che verrà inviata</span>
              <strong>{preview.quantity ?? '—'}</strong>
            </div>
            {preview.residualQuantity && (
              <div className="manual-order__row manual-order__row--muted">
                <span>resterà in portafoglio</span>
                <span>{preview.residualQuantity}</span>
              </div>
            )}
            <div className="manual-order__row">
              <span>Controvalore stimato</span>
              <strong>{preview.estimatedAmount ?? '—'}</strong>
            </div>
            <div className="manual-order__row">
              <span>Limiti operativi</span>
              <span>{preview.limitsSummary}</span>
            </div>
            <div className="manual-order__row">
              <span>Ambiente</span>
              <Badge tone={isLive ? 'negative' : 'neutral'}>{preview.environment}</Badge>
            </div>

            {preview.warnings.map((code) => (
              <Alert key={code} variant="warning" title="Da tenere presente">
                {WARNING_LABEL[code] ?? code}
              </Alert>
            ))}

            {preview.blockers.length > 0 && (
              <Alert variant="error" title="L'ordine non può partire">
                <ul>
                  {preview.blockers.map((code) => (
                    <li key={code}>
                      {BLOCKER_LABEL[code] ?? code} <code>{code}</code>
                    </li>
                  ))}
                </ul>
              </Alert>
            )}

            {preview.canSubmit && isLive && (
              <Alert variant="warning" title="Ordine su denaro reale">
                Questa connessione è in ambiente <strong>live</strong>: l&apos;ordine verrà eseguito con
                denaro vero, e non è annullabile una volta eseguito.
              </Alert>
            )}
          </div>
        )}

        {place.error && (
          <Alert variant="error" title="Ordine non inviato">
            {errorMessage(place.error, 'Errore imprevisto')}
          </Alert>
        )}

        <div className="manual-order__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={place.isPending}>
            Annulla
          </Button>
          <Button
            type="button"
            // Variante di pericolo solo su denaro vero: usarla anche in demo la
            // svuoterebbe di significato proprio dove serve.
            variant={isLive ? 'danger' : 'primary'}
            onClick={submit}
            disabled={!preview?.canSubmit || place.isPending}
          >
            {place.isPending
              ? 'Invio…'
              : isLive
                ? 'Invia ordine reale'
                : `Invia ordine (${preview?.environment ?? 'demo'})`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
